/**
 * The 项目参数 edit form: its state (strings, as typed), conversion to the API's settings, and the
 * field-by-field change list shown before saving.
 *
 * Ratios are typed as percent ("7") and stored as fractions (0.07); amounts and ARR are dollars. The
 * service re-checks everything; the checks here only make mistakes visible before the round trip.
 */
import type { DiscountMethod, InvestmentProgramData, InvestmentProgramSettings } from '../client/CpaClient';
import { amountRange, percent, usd } from './programs';

export interface TierForm {
  minArr: string;
  maxArr: string;
  /** Percent per year, comma separated: "3, 4, 5" = year 1 3%, year 2 4%, year 3 5%. */
  ratios: string;
}

export interface ProgramForm {
  name: string;
  priority: string;
  ppaType: string;
  discountCycle: string;
  discountType: string;
  minRatio: string;
  maxRatio: string;
  minAmount: string;
  maxAmount: string;
  tiers: TierForm[];
  qualifierMin: string;
  qualifierMax: string;
  exclusiveGlobal: string[];
  exclusiveChina: string[];
  /** One per line. */
  contacts: string;
  wikis: string;
  applicationLinks: string;
  sla: string;
}

export type FormErrors = Partial<Record<keyof ProgramForm, string>>;

const str = (v: number | undefined | null) => (v == null ? '' : String(v));
const pct = (v: number | undefined | null) => (v == null ? '' : percent(v));
const lines = (v: string[] | undefined) => (v ?? []).join('\n');

export const EMPTY_TIER: TierForm = { minArr: '', maxArr: '', ratios: '' };

/** The form for an existing program, or a blank one for a new program. */
export function fromProgram(p?: InvestmentProgramData): ProgramForm {
  const m = p?.discountMethod;
  return {
    name: p?.name ?? '',
    priority: str(p?.priority),
    ppaType: p?.ppaType ?? 'non_ppa',
    discountCycle: p?.discountCycle ?? 'recurring',
    discountType: m?.discountType ?? 'ratio',
    minRatio: pct(m?.minRatio),
    maxRatio: pct(m?.maxRatio),
    minAmount: str(m?.minAmount),
    maxAmount: str(m?.maxAmount),
    tiers: (m?.table ?? []).map((r) => ({
      minArr: str(r.minArr),
      maxArr: str(r.maxArr),
      ratios: (r.ratiosByYear ?? []).map((y) => pct(y.ratio)).join(', '),
    })),
    qualifierMin: str(p?.qualifier?.min),
    qualifierMax: str(p?.qualifier?.max),
    exclusiveGlobal: p?.mutuallyExclusive?.global ?? [],
    exclusiveChina: p?.mutuallyExclusive?.china ?? [],
    contacts: lines(p?.contacts),
    wikis: lines(p?.wikis),
    applicationLinks: lines(p?.applicationLinks),
    sla: p?.sla ?? '',
  };
}

/** Parses a number field; `undefined` when blank, `NaN` when not a number. */
function num(v: string): number | undefined {
  const t = v.trim().replace(/,/g, '');
  return t === '' ? undefined : Number(t);
}

/** Percent text → fraction, rounded so "7" is exactly 0.07. */
function fraction(v: string): number | undefined {
  const n = num(v);
  return n === undefined || Number.isNaN(n) ? n : Math.round(n * 1e6) / 1e8;
}

const splitLines = (v: string) =>
  v
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);

/**
 * The settings to send, or the problems that stop it. Error messages are i18n keys.
 */
export function toSettings(form: ProgramForm): { settings?: InvestmentProgramSettings; errors: FormErrors } {
  const errors: FormErrors = {};
  const bad = (n: number | undefined) => n === undefined || Number.isNaN(n);

  if (!form.name.trim()) errors.name = 'formRequired';
  const priority = num(form.priority);
  if (bad(priority) || !Number.isInteger(priority) || priority! < 0) errors.priority = 'formPriority';

  let method: DiscountMethod = { discountType: form.discountType as DiscountMethod['discountType'] };
  if (form.discountType === 'ratio') {
    const min = fraction(form.minRatio);
    const max = fraction(form.maxRatio);
    if (bad(min)) errors.minRatio = 'formNumber';
    if (bad(max)) errors.maxRatio = 'formNumber';
    else if (max! > 1 || (!bad(min) && (min! < 0 || min! > max!))) errors.maxRatio = 'formRatioRange';
    method = { ...method, minRatio: min, maxRatio: max };
  } else if (form.discountType === 'amount') {
    const min = num(form.minAmount);
    const max = num(form.maxAmount);
    if (bad(min)) errors.minAmount = 'formNumber';
    if (bad(max)) errors.maxAmount = 'formNumber';
    else if (!bad(min) && (min! < 0 || min! > max!)) errors.maxAmount = 'formMinMax';
    method = { ...method, minAmount: min, maxAmount: max };
  } else if (form.discountType === 'table') {
    if (form.tiers.length === 0) errors.tiers = 'formTiersEmpty';
    const table = form.tiers.map((tier) => {
      const ratios = tier.ratios
        .split(/[,，\s]+/)
        .filter(Boolean)
        .map(fraction);
      const minArr = num(tier.minArr);
      const maxArr = num(tier.maxArr);
      if (bad(minArr) || bad(maxArr) || minArr! >= maxArr! || ratios.length === 0 || ratios.some(bad)) {
        errors.tiers = 'formTier';
      }
      return {
        minArr,
        maxArr,
        ratiosByYear: ratios.map((ratio, i) => ({ year: i + 1, ratio })),
      };
    });
    method = { ...method, table };
  }

  const qMin = num(form.qualifierMin);
  const qMax = num(form.qualifierMax);
  if (Number.isNaN(qMin)) errors.qualifierMin = 'formNumber';
  if (Number.isNaN(qMax)) errors.qualifierMax = 'formNumber';
  else if (qMin !== undefined && qMax !== undefined && qMin > qMax) errors.qualifierMax = 'formMinMax';

  const wikis = splitLines(form.wikis);
  const applicationLinks = splitLines(form.applicationLinks);
  if (wikis.some((l) => !l.startsWith('https://'))) errors.wikis = 'formHttps';
  if (applicationLinks.some((l) => !l.startsWith('https://'))) errors.applicationLinks = 'formHttps';

  if (Object.keys(errors).length > 0) return { errors };
  return {
    errors,
    settings: {
      name: form.name.trim(),
      priority,
      ppaType: form.ppaType as InvestmentProgramSettings['ppaType'],
      discountCycle: form.discountCycle as InvestmentProgramSettings['discountCycle'],
      discountMethod: method,
      qualifier: qMin === undefined && qMax === undefined ? undefined : { min: qMin, max: qMax },
      mutuallyExclusive: { global: form.exclusiveGlobal, china: form.exclusiveChina },
      contacts: splitLines(form.contacts),
      wikis,
      applicationLinks,
      sla: form.sla.trim() || undefined,
    },
  };
}

/** The fields shown in the change list, in form order. */
export const DIFF_FIELDS = [
  'name',
  'priority',
  'ppaType',
  'discountCycle',
  'discount',
  'qualifier',
  'exclusiveGlobal',
  'exclusiveChina',
  'contacts',
  'sla',
  'wikis',
  'applicationLinks',
] as const;
export type DiffField = (typeof DIFF_FIELDS)[number];

export interface FieldChange {
  field: DiffField;
  before: string;
  after: string;
}

type Comparable = Pick<
  InvestmentProgramData,
  | 'name'
  | 'priority'
  | 'ppaType'
  | 'discountCycle'
  | 'discountMethod'
  | 'qualifier'
  | 'mutuallyExclusive'
  | 'contacts'
  | 'sla'
  | 'wikis'
  | 'applicationLinks'
>;

/** One line describing a discount method, e.g. "比例 3 – 7 %". `label` maps a discount type to its name. */
export function describeDiscount(m: DiscountMethod | undefined, label: (type: string) => string): string {
  if (!m?.discountType) return '';
  switch (m.discountType) {
    case 'ratio':
      return `${label('ratio')} ${m.minRatio == null ? '?' : percent(m.minRatio)} – ${
        m.maxRatio == null ? '?' : percent(m.maxRatio)
      } %`;
    case 'amount':
      return `${label('amount')} ${amountRange(m) ?? ''}`;
    case 'table':
      return `${label('table')}: ${(m.table ?? [])
        .map(
          (r) =>
            `${r.minArr == null ? '?' : usd(r.minArr)}–${r.maxArr == null ? '?' : usd(r.maxArr)} ${(
              r.ratiosByYear ?? []
            )
              .map((y) => (y.ratio == null ? '?' : percent(y.ratio)))
              .join('/')}%`,
        )
        .join('; ')}`;
    default:
      return label(m.discountType);
  }
}

/**
 * What saving would change, field by field. `before` is absent for a new program, so every filled
 * field shows as a change. `show` renders enum values (PPA type, cycle, discount type) for people.
 */
export function diffSettings(
  before: Comparable | undefined,
  after: Comparable,
  show: (kind: 'ppaType' | 'discountCycle' | 'discountType', value: string) => string,
): FieldChange[] {
  const view = (p: Comparable | undefined): Record<DiffField, string> => {
    const q = p?.qualifier;
    return {
      name: p?.name ?? '',
      priority: str(p?.priority),
      ppaType: p?.ppaType ? show('ppaType', p.ppaType) : '',
      discountCycle: p?.discountCycle ? show('discountCycle', p.discountCycle) : '',
      discount: describeDiscount(p?.discountMethod, (type) => show('discountType', type)),
      qualifier:
        q?.min == null && q?.max == null
          ? ''
          : `${q?.min == null ? '' : usd(q.min)} – ${q?.max == null ? '' : usd(q.max)}`,
      exclusiveGlobal: [...(p?.mutuallyExclusive?.global ?? [])].sort().join(', '),
      exclusiveChina: [...(p?.mutuallyExclusive?.china ?? [])].sort().join(', '),
      contacts: (p?.contacts ?? []).join(', '),
      sla: p?.sla ?? '',
      wikis: (p?.wikis ?? []).join(', '),
      applicationLinks: (p?.applicationLinks ?? []).join(', '),
    };
  };
  const a = view(before);
  const b = view(after);
  return DIFF_FIELDS.filter((f) => a[f] !== b[f]).map((field) => ({
    field,
    before: a[field],
    after: b[field],
  }));
}

/** What a failed save means for the person saving. Returns an i18n key and, when useful, the server message. */
export function saveErrorOf(e: unknown): { key: string; detail?: string } {
  const err = e as { name?: string; message?: string; $metadata?: { httpStatusCode?: number } } | null;
  switch (err?.name) {
    case 'ConflictException':
      return { key: 'saveConflict', detail: err.message };
    case 'InvalidInputException':
      return { key: 'saveInvalid', detail: err.message };
    case 'AccessDeniedException':
      // The service refuses both non-admins and stages where program writes are switched off.
      return { key: /switched off/i.test(err.message ?? '') ? 'saveDisabled' : 'programsDenied' };
    default:
      return { key: 'saveFailed', detail: err?.message };
  }
}
